import { configClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';

import {
  toAdminDashboard,
  toDashboardToggleBody,
  toSubsidiaryDashboardToggleBody,
} from '../mappers/dashboard-configuration.mapper';
import type { DashboardResponse } from '../types/dashboard-configuration.api.types';
import type {
  AdminDashboard,
  DashboardToggleRequest,
  SubsidiaryDashboardToggleRequest,
} from '../types/dashboard-configuration.types';

/** Header agentic-config expects for platform scoping. */
const PLATFORM_HEADER = 'X-Platform-ID';

/**
 * Service-layer wrappers for the Dashboard Configuration endpoints in
 * agentic-config. List is per-platform via X-Platform-ID; each toggle is its
 * own PUT against `/dashboards/{id}`.
 */

/** Lists all admin-gated dashboards with the platform's enabled state. */
export async function fetchDashboards(platformId: string): Promise<AdminDashboard[]> {
  const wire = await configClient
    .get(endpoints.configuration.dashboards(), {
      headers: { [PLATFORM_HEADER]: platformId },
    })
    .json<DashboardResponse[]>();
  return wire.map(toAdminDashboard);
}

/** Toggles a single dashboard's enabled state for the platform. */
export async function toggleDashboard(
  platformId: string,
  dashboardId: string,
  payload: DashboardToggleRequest,
): Promise<AdminDashboard> {
  const wire = await configClient
    .put(endpoints.configuration.dashboardById(dashboardId), {
      headers: { [PLATFORM_HEADER]: platformId },
      json: toDashboardToggleBody(payload),
    })
    .json<DashboardResponse>();
  return toAdminDashboard(wire);
}

/** Per-dashboard outcome from a batch save — either the updated row (success)
 *  or an error keyed back to the id so the page can report which rows failed. */
export interface DashboardBatchOutcome {
  id: string;
  status: 'fulfilled' | 'rejected';
  /** Present when `status === 'fulfilled'`. */
  updated?: AdminDashboard;
  /** Present when `status === 'rejected'`. */
  reason?: unknown;
}

/** Aggregate result of a batch save. Always includes the per-row outcomes so
 *  the caller can surface partial failures; the backend has no atomic bulk
 *  endpoint, so the FE has to acknowledge that some rows can commit while
 *  others fail. */
export interface DashboardBatchResult {
  outcomes: DashboardBatchOutcome[];
  /** True iff every change succeeded. */
  allSucceeded: boolean;
  /** True iff at least one change succeeded — used to decide whether the cache
   *  needs invalidation even on overall failure. */
  anySucceeded: boolean;
}

/**
 * Batches a set of toggle changes for the page's Save Changes action. The real
 * backend has no bulk endpoint, so we fan out one PUT per changed row using
 * `Promise.allSettled` and surface per-row outcomes — never silently dropping
 * partial success. The caller invalidates the dashboards query on any settled
 * fulfillment so the next read reflects the actual server state, not a
 * client-merged optimistic guess.
 */
export async function toggleDashboardsBatch(
  platformId: string,
  modifiedBy: string,
  changes: { id: string; enabled: boolean }[],
): Promise<DashboardBatchResult> {
  const settled = await Promise.allSettled(
    changes.map((c) => toggleDashboard(platformId, c.id, { enabled: c.enabled, modifiedBy })),
  );
  const outcomes: DashboardBatchOutcome[] = settled.map((res, i) => {
    const id = changes[i]?.id ?? '';
    if (res.status === 'fulfilled') return { id, status: 'fulfilled', updated: res.value };
    // Promise.allSettled's `reason` is typed `any`; widen to `unknown` so
    // downstream consumers can't accidentally use it without narrowing.
    const reason: unknown = res.reason;
    return { id, status: 'rejected', reason };
  });
  const allSucceeded = outcomes.every((o) => o.status === 'fulfilled');
  const anySucceeded = outcomes.some((o) => o.status === 'fulfilled');
  return { outcomes, allSucceeded, anySucceeded };
}

// ---------------------------------------------------------------------------
// Subsidiary dashboards — resolved (override or platform default) list for
// the active subsidiary. `X-Subsidiary-ID` is attached automatically by
// `subsidiaryInterceptor`; only the platform header is set explicitly here.
// ---------------------------------------------------------------------------

/** Lists all admin-gated dashboards resolved for the active subsidiary. */
export async function fetchSubsidiaryDashboards(platformId: string): Promise<AdminDashboard[]> {
  const wire = await configClient
    .get(endpoints.configuration.subsidiaryDashboards(), {
      headers: { [PLATFORM_HEADER]: platformId },
    })
    .json<DashboardResponse[]>();
  return wire.map(toAdminDashboard);
}

/** Sets (or replaces) the active subsidiary's override for a single
 *  dashboard. Confirmed contract: `{ enabled, reason?, user_id, user_name }`
 *  — `reason` optional, `user_id`/`user_name` required (unlike the global
 *  toggle's single `modified_by`). */
export async function toggleSubsidiaryDashboard(
  platformId: string,
  dashboardId: string,
  payload: SubsidiaryDashboardToggleRequest,
): Promise<AdminDashboard> {
  const wire = await configClient
    .put(endpoints.configuration.subsidiaryDashboardById(dashboardId), {
      headers: { [PLATFORM_HEADER]: platformId },
      json: toSubsidiaryDashboardToggleBody(payload),
    })
    .json<DashboardResponse>();
  return toAdminDashboard(wire);
}

/** Batches subsidiary toggle changes for the page's Save Changes action —
 *  mirrors `toggleDashboardsBatch` exactly, just against the subsidiary
 *  per-row endpoint (still no bulk endpoint, so still `Promise.allSettled`).
 *  No per-row `reason` capture in the UI today, so it's always omitted. */
export async function toggleSubsidiaryDashboardsBatch(
  platformId: string,
  userId: string,
  userName: string,
  changes: { id: string; enabled: boolean }[],
): Promise<DashboardBatchResult> {
  const settled = await Promise.allSettled(
    changes.map((c) =>
      toggleSubsidiaryDashboard(platformId, c.id, { enabled: c.enabled, userId, userName }),
    ),
  );
  const outcomes: DashboardBatchOutcome[] = settled.map((res, i) => {
    const id = changes[i]?.id ?? '';
    if (res.status === 'fulfilled') return { id, status: 'fulfilled', updated: res.value };
    const reason: unknown = res.reason;
    return { id, status: 'rejected', reason };
  });
  const allSucceeded = outcomes.every((o) => o.status === 'fulfilled');
  const anySucceeded = outcomes.some((o) => o.status === 'fulfilled');
  return { outcomes, allSucceeded, anySucceeded };
}

export interface ClearSubsidiaryDashboardOverrideArgs {
  userId: string;
  userName: string;
  reason?: string;
}

/** Clears the active subsidiary's override for a single dashboard, reverting
 *  it to the platform default. Confirmed contract: no body — `dashboard_id`
 *  is a path param (already in the URL); `user_id`/`user_name` (required)
 *  and `reason` (optional, max 1000 chars) are query params. */
export async function clearSubsidiaryDashboardOverride(
  platformId: string,
  dashboardId: string,
  args: ClearSubsidiaryDashboardOverrideArgs,
): Promise<void> {
  const searchParams: Record<string, string> = {
    user_id: args.userId,
    user_name: args.userName,
  };
  if (args.reason) searchParams.reason = args.reason;
  await configClient.delete(endpoints.configuration.subsidiaryDashboardById(dashboardId), {
    headers: { [PLATFORM_HEADER]: platformId },
    searchParams,
  });
}
